/**
 * Per-trap application logic.
 *
 * Each function mutates the local RunState directly. Called by
 * trapSystem.ts while processing pending incoming traps.
 */

import type { RunState } from "../game/types";
import type { GameController } from "../game/stateMachine";
import { Rng } from "../game/rng";
import { BOARD_SIZE } from "../game/constants";
import {
  LOCKDOWN_TILE_COUNT,
  COIN_TAX_BURN_AMOUNT,
  DOOM_SURGE_DRAIN_SEC,
  TRAP_CONFIGS
} from "./versusConstants";
import type { TrapKind } from "./types";

// ── Lockdown Chains ─────────────────────────────────────────────────────────

/**
 * Lock `LOCKDOWN_TILE_COUNT` random unlocked tiles on the board (lockHp=1).
 * Tile selection is deterministic: seeded from `matchSeed + trapSeq` so both
 * clients produce the same result.
 */
export function applyLockdown(run: RunState, matchSeed: number, trapSeq: number): void {
  const rng = new Rng((matchSeed + trapSeq) >>> 0);

  // Collect all unlocked tile positions
  const candidates: Array<{ row: number; col: number }> = [];
  for (let r = 0; r < BOARD_SIZE; r++) {
    for (let c = 0; c < BOARD_SIZE; c++) {
      const cell = run.board.cells[r]?.[c];
      if (cell != null && cell.tile != null && cell.tile.lockHp === 0) {
        candidates.push({ row: r, col: c });
      }
    }
  }

  // Shuffle and pick up to LOCKDOWN_TILE_COUNT
  rng.shuffleInPlace(candidates);
  const count = Math.min(LOCKDOWN_TILE_COUNT, candidates.length);
  for (let i = 0; i < count; i++) {
    const { row, col } = candidates[i];
    run.board.cells[row][col].tile!.lockHp = 1;
  }
}

// ── Fog Overlay ─────────────────────────────────────────────────────────────

/**
 * Returns the fog duration in seconds.
 * The caller is responsible for tracking the fog timer on the match state.
 */
export function getFogDurationSec(): number {
  return TRAP_CONFIGS.FOG_OVERLAY.durationSec ?? 8;
}

// ── Coin Tax ────────────────────────────────────────────────────────────────

/** Burn coins from the target player. Cannot go below 0. */
export function applyCoinTax(run: RunState): void {
  run.cashBalance = Math.max(0, run.cashBalance - COIN_TAX_BURN_AMOUNT);
}

// ── Groove Drain ────────────────────────────────────────────────────────────

/** Drain the target's groove meter and cancel any armed/active groove state. */
export function applyGrooveDrain(run: RunState): void {
  if (run.groove != null) {
    run.groove.groove = 0;
    run.groove.armed = false;
    run.groove.readyWindowSec = 0;
    run.groove.active = false;
    run.groove.activeRemainingSec = 0;
    run.groove.multiplier = 1;
    run.groove.freeRotationsRemaining = 0;
  }
}

// ── Doom Surge ──────────────────────────────────────────────────────────────

/** Drain seconds from the target's doom clock. Cannot go below 0. */
export function applyDoomSurge(run: RunState): void {
  run.doomClockSec = Math.max(0, run.doomClockSec - DOOM_SURGE_DRAIN_SEC);
  run.lives = Math.max(0, Math.ceil(run.doomClockSec / 20));
}

// ── Dispatcher ──────────────────────────────────────────────────────────────

/**
 * Apply a single trap effect to the given run state.
 * Returns `fogDurationSec` if a fog trap was applied (caller manages the timer),
 * otherwise returns 0.
 */
export function applyTrapEffect(
  kind: TrapKind,
  run: RunState,
  matchSeed: number,
  trapSeq: number,
  targetController?: GameController
): number {
  switch (kind) {
    case "LOCKDOWN_CHAINS":
      applyLockdown(run, matchSeed, trapSeq);
      return 0;
    case "FOG_OVERLAY":
      return getFogDurationSec();
    case "COIN_TAX":
      applyCoinTax(run);
      return 0;
    case "GROOVE_DRAIN":
      if (targetController != null) {
        targetController.applyGrooveDrainTrap();
      } else {
        applyGrooveDrain(run);
      }
      return 0;
    case "DOOM_SURGE":
      applyDoomSurge(run);
      return 0;
  }
}
